import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet, type HealthResponse } from '../api/client';
import { MyMembership } from '../api/types';
import { useAuth } from '../auth/useAuth';
import { ptBR } from '../i18n/pt-BR';

type HealthState = { kind: 'loading' } | { kind: 'ok'; time: string } | { kind: 'down' };

interface MyMembershipsResponse {
  memberships: MyMembership[];
}

export default function HomePage() {
  const { user, logout } = useAuth();
  const [health, setHealth] = useState<HealthState>({ kind: 'loading' });
  const membershipsQuery = useQuery({
    queryKey: ['me', 'memberships'],
    queryFn: () => apiGet<MyMembershipsResponse>('/me/memberships'),
  });

  useEffect(() => {
    let cancelled = false;
    apiGet<HealthResponse>('/health')
      .then((res) => {
        if (!cancelled) setHealth({ kind: 'ok', time: res.time });
      })
      .catch(() => {
        if (!cancelled) setHealth({ kind: 'down' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const memberships = membershipsQuery.data?.memberships ?? [];

  return (
    <main
      style={{
        fontFamily: 'system-ui, sans-serif',
        maxWidth: 480,
        margin: '4rem auto',
        padding: '0 1rem',
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <h1 style={{ marginBottom: 4 }}>{ptBR.appName}</h1>
          <p style={{ color: '#666', marginTop: 0 }}>{ptBR.tagline}</p>
        </div>
        <button onClick={() => void logout()}>{ptBR.auth.logout}</button>
      </header>

      <section
        style={{ marginTop: 16, padding: 16, border: '1px solid #ddd', borderRadius: 8 }}
      >
        <h2 style={{ margin: '0 0 8px', fontSize: 16 }}>
          {memberships.length > 1 ? ptBR.home.myCompanies : ptBR.home.myCompany}
        </h2>
        {membershipsQuery.isLoading && <p style={{ margin: 0 }}>{ptBR.common.loading}</p>}
        {!membershipsQuery.isLoading && memberships.length === 0 && (
          <p style={{ margin: 0, color: '#666' }}>{ptBR.home.noCompany}</p>
        )}
        {memberships.length > 0 && (
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {memberships.map((m) => (
              <li key={m.tenantId} style={{ fontWeight: 600 }}>
                {m.tenantName}
              </li>
            ))}
          </ul>
        )}
      </section>

      {user?.isPlatformAdmin && (
        <p>
          <Link to="/admin">{ptBR.admin.goToAdmin}</Link>
        </p>
      )}

      <section
        style={{ marginTop: 16, padding: 16, border: '1px solid #ddd', borderRadius: 8 }}
      >
        {health.kind === 'loading' && <p>{ptBR.health.checking}</p>}
        {health.kind === 'ok' && (
          <>
            <p style={{ fontWeight: 600, color: '#137333' }}>{ptBR.health.ok}</p>
            <p style={{ margin: 0, color: '#666' }}>{ptBR.health.dbUp}</p>
            <p style={{ margin: '4px 0 0', color: '#999', fontSize: 13 }}>{health.time}</p>
          </>
        )}
        {health.kind === 'down' && (
          <>
            <p style={{ fontWeight: 600, color: '#c5221f' }}>{ptBR.health.down}</p>
            <p style={{ margin: 0, color: '#666' }}>{ptBR.health.dbDown}</p>
          </>
        )}
      </section>
    </main>
  );
}
